'use client';

import { forwardRef } from 'react';
import { CREATE_OPTION_VALUE, MultiSelect } from '@highlight-cp/core-ui';
import type {
  MultiSelectProps,
  CreatableSelectProps as CreatableFlowProps,
} from '@highlight-cp/core-ui';

import { useCreatableOptions } from '../creatable-select/use-creatable-options';

export type CreatableMultiSelectProps = MultiSelectProps & CreatableFlowProps;

/**
 * ### CreatableMultiSelect
 *
 * @description
 * The core-ui `MultiSelect` with the v6-compat `creatable` flow — pass `creatable` to let
 * users add a new option from the current search query, `getCreateLabel` to label the
 * synthetic "create" row, and `onCreate` to resolve the created value. `onChange` receives
 * the resolved value in place of the create row. Same restricted style API as `MultiSelect`.
 *
 * @example
 * <CreatableMultiSelect creatable getCreateLabel={(q) => `+ Create ${q}`} onCreate={handleCreate} />
 *
 * @param props - {@link CreatableMultiSelectProps}
 */
export const CreatableMultiSelect = forwardRef<HTMLInputElement, CreatableMultiSelectProps>(
  (props, ref) => {
    const { creatable, getCreateLabel, onCreate, data, onChange, searchable, ...rest } = props;
    const { augmentedData, searchProps, resolveCreate } = useCreatableOptions({
      creatable,
      data,
      getCreateLabel,
      onCreate,
    });

    const handleChange: MultiSelectProps['onChange'] = (values) => {
      if (creatable && values.includes(CREATE_OPTION_VALUE)) {
        const createdValue = resolveCreate();
        onChange?.(values.map((value) => (value === CREATE_OPTION_VALUE ? createdValue : value)));
        return;
      }
      onChange?.(values);
    };

    return (
      <MultiSelect
        ref={ref}
        {...rest}
        data={augmentedData}
        onChange={handleChange}
        searchable={creatable || searchable}
        {...searchProps}
      />
    );
  },
);
CreatableMultiSelect.displayName = 'CreatableMultiSelect';
